/**
 * workspace/stubs.ts — CLI workspace tool stubs.
 *
 * Implements the 9 council workspace tools (`createPhase`, `createTask`,
 * `updateTask`, `addIdea`, `createMilestone`, `createDocument`,
 * `searchDocuments`, `linkDocuments`, `getDocumentBacklinks`) as
 * filesystem-backed stubs that persist to `.zelari/` at the project root.
 *
 * Replaces the AnathemaBrain Electron-only ctx (e.g. `ctx.addIdea(...)`)
 * with a `WorkspaceContext` that writes Markdown + YAML frontmatter files.
 *
 * @see docs/plans/2026-07-01-council-workspace-cli-stubs.md
 */

import { existsSync, readdirSync, writeFileSync, readFileSync } from 'node:fs';
import { join, basename } from 'node:path';
import type { EnhancedToolDefinition } from '@zelari/core/types';
import { workspaceMutex } from './storage.js';
import {
  resolveWorkspaceRoot,
  workspaceFile,
  workspaceArtifact,
  projectName as deriveProjectName,
} from './paths.js';
import { Storage } from './storage.js';
import type {
  WorkspaceContext,
  PlanFrontmatter,
  AdrFrontmatter,
  RiskFrontmatter,
  ReviewFrontmatter,
  DocFrontmatter,
} from './types.js';

// ── Context factory ───────────────────────────────────────────────────

/**
 * Build a WorkspaceContext rooted at the project's `.zelari/` directory.
 * Use this in the CLI's `runCouncilPure` ctx slot.
 */
export function createWorkspaceContext(projectRoot: string = process.cwd()): WorkspaceContext {
  const rootDir = resolveWorkspaceRoot(projectRoot);
  return {
    rootDir,
    projectRoot,
    storage: new Storage(),
  };
}

// ── Plan (plan.md) helpers ────────────────────────────────────────────

interface PlanSummary {
  phases: PlanFrontmatter[];
  tasks: PlanFrontmatter[];
  milestones: PlanFrontmatter[];
}

function readPlan(ctx: WorkspaceContext): PlanSummary {
  const path = workspaceFile(ctx.rootDir, 'plan');
  const doc = ctx.storage.readIfExists<PlanFrontmatter>(path);
  if (!doc) return { phases: [], tasks: [], milestones: [] };
  // The plan.md body is markdown; the frontmatter is a single "summary" object.
  // We treat the file as a single doc with `kind: plan` and a body listing items.
  // For our needs we just persist individual items to disk separately.
  // But for v1 we serialize the entire plan as one doc with a list of phases/tasks/milestones in the body.
  // Simpler: we store ONE phase/task/milestone per file under `.zelari/plan/<id>.md`.
  // TODO: refactor for v2 if plan gets large.
  const items: PlanFrontmatter[] = [];
  // For now, parse the doc.meta as the entire plan state (kind=plan, has children fields).
  const meta = doc.meta as PlanFrontmatter & {
    phases?: PlanFrontmatter[];
    tasks?: PlanFrontmatter[];
    milestones?: PlanFrontmatter[];
  };
  if (meta.phases) items.push(...meta.phases);
  return { phases: meta.phases ?? [], tasks: meta.tasks ?? [], milestones: meta.milestones ?? [] };
}

/**
 * Write the entire plan (phases + tasks + milestones) as one file.
 * Simpler than per-item files for v1.
 */
function writePlan(
  ctx: WorkspaceContext,
  summary: PlanSummary,
): void {
  const path = workspaceFile(ctx.rootDir, 'plan');
  const meta: PlanFrontmatter = {
    kind: 'phase',
    id: '_plan_summary',
    order: -1,
    tags: ['plan-summary'],
  };
  // We pack phases/tasks/milestones in a synthetic frontmatter using flow-style.
  // Easier: just store each item's key fields inline as comments? No — keep them
  // parseable. We use a special frontmatter shape with arrays.
  const summaryMeta = {
    kind: 'plan-summary',
    id: '_plan',
    updatedAt: new Date().toISOString(),
    phases: summary.phases,
    tasks: summary.tasks,
    milestones: summary.milestones,
  };
  const body = renderPlanBody(summary);
  ctx.storage.write(path, summaryMeta, body);
}

/** Render the plan.md body in human-readable Markdown. */
function renderPlanBody(summary: PlanSummary): string {
  const lines: string[] = [];
  lines.push(`# Execution Plan`);
  lines.push('');
  lines.push(`> Auto-generated by Zelari Code council. Do not edit by hand.`);
  lines.push('');

  // Phases
  lines.push(`## Phases`);
  lines.push('');
  if (summary.phases.length === 0) {
    lines.push(`_(none yet)_`);
  } else {
    for (const phase of [...summary.phases].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))) {
      lines.push(`### ${phase.order ?? '?'}. ${phase.id} \`${phase.color ?? ''}\``);
      lines.push('');
    }
  }
  lines.push('');

  // Tasks grouped by phase
  lines.push(`## Tasks`);
  lines.push('');
  if (summary.tasks.length === 0) {
    lines.push(`_(none yet)_`);
  } else {
    const byPhase = new Map<string, PlanFrontmatter[]>();
    for (const task of summary.tasks) {
      const pid = task.phaseId ?? '_unassigned';
      if (!byPhase.has(pid)) byPhase.set(pid, []);
      byPhase.get(pid)!.push(task);
    }
    for (const [phaseId, tasks] of byPhase) {
      lines.push(`### Phase: \`${phaseId}\``);
      lines.push('');
      for (const t of tasks) {
        const status = t.status ?? 'pending';
        const pri = t.priority ?? 'medium';
        lines.push(`- [ ] **${t.id}** _(${status}, ${pri})_`);
      }
      lines.push('');
    }
  }
  lines.push('');

  // Milestones
  lines.push(`## Milestones`);
  lines.push('');
  if (summary.milestones.length === 0) {
    lines.push(`_(none yet)_`);
  } else {
    for (const m of summary.milestones) {
      lines.push(`- **${m.id}** → ${m.targetVersion ?? '?'}`);
    }
  }
  lines.push('');

  return lines.join('\n');
}

// ── ADR (decisions/) helpers ──────────────────────────────────────────

function nextAdrId(ctx: WorkspaceContext): string {
  const decisionsDir = join(ctx.rootDir, 'decisions');
  if (!existsSync(decisionsDir)) return '001';
  const existing = readdirSync(decisionsDir)
    .filter((f) => f.endsWith('.md'))
    .map((f) => f.match(/^(\d+)-/))
    .filter((m): m is RegExpMatchArray => !!m)
    .map((m) => parseInt(m[1]!, 10));
  const max = existing.length === 0 ? 0 : Math.max(...existing);
  return String(max + 1).padStart(3, '0');
}

function slugify(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 60);
}

// ── Stub factory ─────────────────────────────────────────────────────

/**
 * Create all 9 workspace stubs bound to the given context.
 * Pass the returned array into `runCouncilPure`'s `config.tools` (via a
 * tool registry), or register them in the global tool registry when
 * running the CLI.
 */
export function createWorkspaceStubs(ctx: WorkspaceContext): EnhancedToolDefinition[] {
  return [
    createPhaseStub(ctx),
    createTaskStub(ctx),
    updateTaskStub(ctx),
    addIdeaStub(ctx),
    createMilestoneStub(ctx),
    createDocumentStub(ctx),
    searchDocumentsStub(ctx),
    linkDocumentsStub(ctx),
    getDocumentBacklinksStub(ctx),
  ];
}

// ── Individual stubs ──────────────────────────────────────────────────

function createPhaseStub(ctx: WorkspaceContext): EnhancedToolDefinition {
  return {
    name: 'createPhase',
    description: 'Create a new execution phase in the project plan. Each phase is a milestone toward shipping a feature.',
    category: 'project',
    parameters: [],
    execute: async (args) => {
      return workspaceMutex.run(`${ctx.rootDir}:plan`, () => {
        const name = (args['name'] as string) ?? 'New Phase';
        const description = (args['description'] as string) ?? '';
        const order = (args['order'] as number) ?? 0;
        const color = (args['color'] as string) ?? '#3b82f6';
        const id = slugify(name) || `phase-${order}`;
        const summary = readPlan(ctx);
        // Check uniqueness
        if (summary.phases.some((p) => p.id === id)) {
          return `Phase "${id}" already exists.`;
        }
        summary.phases.push({
          kind: 'phase',
          id,
          name,
          description,
          order,
          color,
        });
        writePlan(ctx, summary);
        return `Phase "${name}" created (id: ${id}, order: ${order}).`;
      });
    },
  };
}

function createTaskStub(ctx: WorkspaceContext): EnhancedToolDefinition {
  return {
    name: 'createTask',
    description: 'Create a task within an existing phase. Tasks have file references, acceptance criteria, and a QA scenario.',
    category: 'project',
    parameters: [],
    execute: async (args) => {
      return workspaceMutex.run(`${ctx.rootDir}:plan`, () => {
        const phaseId = (args['phaseId'] as string) ?? args['phase'] as string;
        const title = (args['title'] as string) ?? 'New Task';
        const description = (args['description'] as string) ?? '';
        const fileRefs = (args['fileRefs'] as string[]) ?? (args['files'] as string[]) ?? [];
        const acceptance = (args['acceptance'] as string[]) ?? [];
        const qaScenario = (args['qaScenario'] as string) ?? (args['qa'] as string) ?? '';
        const priority = (args['priority'] as string) ?? 'medium';
        if (!phaseId) return 'Task requires a phaseId.';
        const summary = readPlan(ctx);
        if (!summary.phases.some((p) => p.id === phaseId)) {
          return `Phase "${phaseId}" not found. Create it first via /createPhase.`;
        }
        const id = `${phaseId}-${slugify(title)}-${summary.tasks.filter((t) => t.phaseId === phaseId).length + 1}`;
        summary.tasks.push({
          kind: 'task',
          id,
          phaseId,
          status: 'pending',
          priority: priority as 'low' | 'medium' | 'high' | 'critical',
        });
        writePlan(ctx, summary);
        // Also write the task body to a per-task file so it doesn't get lost
        const taskPath = join(ctx.rootDir, 'plan-tasks', `${id}.md`);
        const meta: PlanFrontmatter = {
          kind: 'task',
          id,
          phaseId,
          status: 'pending',
          priority: priority as 'low' | 'medium' | 'high' | 'critical',
          tags: fileRefs,
        };
        const body = [
          `# ${title}`,
          '',
          description,
          '',
          `## File references`,
          ...fileRefs.map((f) => `- \`${f}\``),
          '',
          `## Acceptance criteria`,
          ...acceptance.map((a) => `- ${a}`),
          '',
          `## QA scenario`,
          '',
          qaScenario || '_(not specified)_',
          '',
        ].join('\n');
        ctx.storage.write(taskPath, meta, body);
        return `Task "${title}" created (id: ${id}) under phase "${phaseId}".`;
      });
    },
  };
}

function updateTaskStub(ctx: WorkspaceContext): EnhancedToolDefinition {
  return {
    name: 'updateTask',
    description: 'Update a task\'s status (pending|in_progress|done|blocked).',
    category: 'project',
    parameters: [],
    execute: async (args) => {
      return workspaceMutex.run(`${ctx.rootDir}:plan`, () => {
        const taskId = args['taskId'] as string;
        const status = args['status'] as string;
        if (!taskId || !status) return 'updateTask requires taskId and status.';
        const valid = ['pending', 'in_progress', 'done', 'blocked'];
        if (!valid.includes(status)) return `Invalid status: ${status}. Must be one of: ${valid.join(', ')}.`;
        const summary = readPlan(ctx);
        const task = summary.tasks.find((t) => t.id === taskId);
        if (!task) return `Task "${taskId}" not found.`;
        task.status = status as 'pending' | 'in_progress' | 'done' | 'blocked';
        writePlan(ctx, summary);
        return `Task "${taskId}" updated to status="${status}".`;
      });
    },
  };
}

function addIdeaStub(ctx: WorkspaceContext): EnhancedToolDefinition {
  return {
    name: 'addIdea',
    description: 'Add an Architecture Decision Record (ADR) to the project. ADRs document the context, decision, and consequences of significant choices.',
    category: 'project',
    parameters: [],
    execute: async (args) => {
      return workspaceMutex.run(`${ctx.rootDir}:adr`, () => {
        const title = (args['title'] as string) ?? 'New Decision';
        const context = (args['context'] as string) ?? (args['content'] as string) ?? '';
        const decision = (args['decision'] as string) ?? title;
        const consequences = (args['consequences'] as string[]) ?? [];
        const tags = (args['tags'] as string[]) ?? [];
        const category = (args['category'] as string) ?? 'General';
        const id = `${nextAdrId(ctx)}-${slugify(title)}`;
        const path = workspaceArtifact(ctx.rootDir, 'decisions', id);
        const meta: AdrFrontmatter = {
          kind: 'adr',
          status: 'proposed',
          id,
          date: new Date().toISOString().slice(0, 10),
          tags,
        };
        const body = [
          `# ADR ${id}: ${title}`,
          '',
          `> Category: ${category}  ·  Status: **proposed**  ·  ${meta.date}`,
          '',
          `## Context`,
          '',
          context,
          '',
          `## Decision`,
          '',
          decision,
          '',
          `## Consequences`,
          '',
          ...consequences.map((c) => `- ${c}`),
          '',
        ].join('\n');
        ctx.storage.write(path, meta, body);
        return `ADR ${id} created: "${title}". Status: proposed. Promote to accepted via /update ADR or manual edit.`;
      });
    },
  };
}

function createMilestoneStub(ctx: WorkspaceContext): EnhancedToolDefinition {
  return {
    name: 'createMilestone',
    description: 'Create a project milestone (a code version that ships when specific acceptance criteria are met).',
    category: 'project',
    parameters: [],
    execute: async (args) => {
      return workspaceMutex.run(`${ctx.rootDir}:plan`, () => {
        const title = (args['title'] as string) ?? 'New Milestone';
        const description = (args['description'] as string) ?? '';
        const dueDate = args['dueDate'] as string | undefined;
        const id = `m-${slugify(title)}`;
        const summary = readPlan(ctx);
        if (summary.milestones.some((m) => m.id === id)) return `Milestone "${id}" already exists.`;
        const version = (args['targetVersion'] as string) ?? dueDate ?? 'TBD';
        summary.milestones.push({
          kind: 'milestone',
          id,
          name: title,
          description,
          dueDate,
          targetVersion: version,
        });
        writePlan(ctx, summary);
        // Also write the body
        const path = join(ctx.rootDir, 'milestones', `${id}.md`);
        const meta: PlanFrontmatter = {
          kind: 'milestone',
          id,
          name: title,
          description,
          dueDate,
          targetVersion: version,
        };
        const body = [
          `# Milestone: ${title}`,
          '',
          description,
          '',
          `Target version: ${version}`,
          '',
        ].join('\n');
        ctx.storage.write(path, meta, body);
        return `Milestone "${title}" created (id: ${id}, version: ${version}).`;
      });
    },
  };
}

function createDocumentStub(ctx: WorkspaceContext): EnhancedToolDefinition {
  return {
    name: 'createDocument',
    description: 'Create a Markdown document in the project docs (README/CHANGELOG/spec draft produced by the council).',
    category: 'vault',
    parameters: [],
    execute: async (args) => {
      return workspaceMutex.run(`${ctx.rootDir}:doc`, () => {
        const title = (args['title'] as string) ?? 'New Document';
        const content = (args['content'] as string) ?? '';
        const tags = (args['tags'] as string[]) ?? [];
        const id = slugify(title) || `doc-${Date.now()}`;
        const path = workspaceArtifact(ctx.rootDir, 'docs', id);
        const meta: DocFrontmatter = {
          kind: 'doc',
          id,
          date: new Date().toISOString().slice(0, 10),
          tags,
        };
        ctx.storage.write(path, meta, content);
        return `Document "${title}" created at docs/${id}.md.`;
      });
    },
  };
}

function searchDocumentsStub(ctx: WorkspaceContext): EnhancedToolDefinition {
  return {
    name: 'searchDocuments',
    description: 'Search all workspace documents (`.zelari/**`) for a keyword. Returns matching files with snippets.',
    category: 'analysis',
    parameters: [],
    execute: async (args) => {
      const query = ((args['query'] as string) ?? '').toLowerCase();
      const limit = (args['limit'] as number) ?? 5;
      if (!query) return 'searchDocuments requires a query.';
      const files = [
        ...ctx.storage.listMarkdown(join(ctx.rootDir, 'decisions')),
        ...ctx.storage.listMarkdown(join(ctx.rootDir, 'docs')),
        ...ctx.storage.listMarkdown(join(ctx.rootDir, 'reviews')),
        workspaceFile(ctx.rootDir, 'plan'),
        workspaceFile(ctx.rootDir, 'risks'),
      ];
      const results: { path: string; snippet: string }[] = [];
      for (const file of files) {
        if (!existsSync(file)) continue;
        const content = readFileSync(file, 'utf8').toLowerCase();
        const idx = content.indexOf(query);
        if (idx < 0) continue;
        const start = Math.max(0, idx - 40);
        const end = Math.min(content.length, idx + query.length + 40);
        const snippet = '…' + readFileSync(file, 'utf8').slice(start, end).replace(/\n/g, ' ').trim() + '…';
        results.push({ path: file, snippet });
        if (results.length >= limit) break;
      }
      if (results.length === 0) return `No matches for "${query}" in workspace.`;
      return results.map((r) => `[${r.path}]\n${r.snippet}`).join('\n\n');
    },
  };
}

function linkDocumentsStub(ctx: WorkspaceContext): EnhancedToolDefinition {
  return {
    name: 'linkDocuments',
    description: 'Link two workspace artifacts. Adds the `related:` field to the source frontmatter so backreferences can be traced.',
    category: 'vault',
    parameters: [],
    execute: async (args) => {
      return workspaceMutex.run(`${ctx.rootDir}:link`, () => {
        const fromId = args['fromId'] as string;
        const toId = args['toId'] as string;
        if (!fromId || !toId) return 'linkDocuments requires fromId and toId.';
        // Find the source file by id
        const allFiles = [
          ...ctx.storage.listMarkdown(join(ctx.rootDir, 'decisions')),
          ...ctx.storage.listMarkdown(join(ctx.rootDir, 'docs')),
          ...ctx.storage.listMarkdown(join(ctx.rootDir, 'reviews')),
        ];
        const source = allFiles.find((f) => {
          try {
            const meta = ctx.storage.read<{ id: string }>(f).meta;
            return meta.id === fromId;
          } catch { return false; }
        });
        if (!source) return `Source "${fromId}" not found.`;
        const doc = ctx.storage.read<AdrFrontmatter | DocFrontmatter | ReviewFrontmatter>(source);
        const related = doc.meta.related ?? [];
        if (!related.includes(toId)) related.push(toId);
        doc.meta.related = related;
        ctx.storage.write(source, doc.meta, doc.body);
        return `Linked ${fromId} → ${toId}.`;
      });
    },
  };
}

function getDocumentBacklinksStub(ctx: WorkspaceContext): EnhancedToolDefinition {
  return {
    name: 'getDocumentBacklinks',
    description: 'List all workspace artifacts that link to the given artifact via their `related:` frontmatter.',
    category: 'analysis',
    parameters: [],
    execute: async (args) => {
      const targetId = args['targetId'] as string;
      if (!targetId) return 'getDocumentBacklinks requires targetId.';
      const allFiles = [
        ...ctx.storage.listMarkdown(join(ctx.rootDir, 'decisions')),
        ...ctx.storage.listMarkdown(join(ctx.rootDir, 'docs')),
        ...ctx.storage.listMarkdown(join(ctx.rootDir, 'reviews')),
      ];
      const backlinks: string[] = [];
      for (const file of allFiles) {
        try {
          const doc = ctx.storage.read<{ id: string; related?: string[] }>(file);
          if (doc.meta.id === targetId) continue;
          if ((doc.meta.related ?? []).includes(targetId)) {
            backlinks.push(doc.meta.id);
          }
        } catch { /* skip */ }
      }
      if (backlinks.length === 0) return `No backlinks to "${targetId}".`;
      return backlinks.map((id) => `- ${id}`).join('\n');
    },
  };
}

/** Convenience: get the workspace root used by the stubs. */
export { resolveWorkspaceRoot, deriveProjectName };